import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@heroui/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { AccountingPeriodStatus } from '@/api/models';
import { PERIOD_STATUS_CHIP } from '../../periodsMeta';

type PeriodStatusChipProps = {
    status: AccountingPeriodStatus;
    /** testid discriminator (the period id). */
    slot: string;
};

/**
 * Soft status pill with a leading state dot — deliberately the same shape and
 * vocabulary as the journal's Draft / Posted / Reversed chip. A closed period is
 * normal bookkeeping, so nothing here reads as an error state.
 */
export const PeriodStatusChip: React.FC<PeriodStatusChipProps> = ({
    status,
    slot,
}) => {
    const { t } = useTranslation('pages');
    const config = PERIOD_STATUS_CHIP[status];

    return (
        <Chip
            className={cn(config.className, 'border-0')}
            size="sm"
            variant="soft"
            {...getNodeTestIdProp(`period-status-${slot}`)}
        >
            <Chip.Label className="flex items-center gap-1.5">
                <span
                    aria-hidden
                    className="size-1.5 rounded-full bg-current opacity-70"
                />
                {t(config.labelKey)}
            </Chip.Label>
        </Chip>
    );
};
